import { useEffect, useCallback } from "react";
import { useCameraCapture } from "@/hooks/useCameraCapture";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, X, AlertCircle } from "lucide-react";

interface CameraDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

export function CameraDialog({ open, onClose, onCapture }: CameraDialogProps) {
  const { videoRef, isStreaming, facingMode, error, startCamera, stopCamera, capturePhoto, switchCamera } =
    useCameraCapture();

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [open]);

  const handleCapture = useCallback(() => {
    const file = capturePhoto();
    if (!file) return;
    stopCamera();
    onCapture(file);
    onClose();
  }, [capturePhoto, stopCamera, onCapture, onClose]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Tomar fotografía
          </DialogTitle>
        </DialogHeader>

        <div className="relative bg-black w-full aspect-video flex items-center justify-center">
          {error ? (
            <div className="flex flex-col items-center gap-3 px-6 text-center">
              <AlertCircle className="h-10 w-10 text-destructive" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={() => startCamera()}>
                Reintentar
              </Button>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={facingMode === "user" ? { transform: "scaleX(-1)" } : undefined}
            />
          )}

          {isStreaming && (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8 rounded-full opacity-80"
              onClick={switchCamera}
              title="Cambiar cámara"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>

        <DialogFooter className="px-4 py-3 flex-row justify-between sm:justify-between gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleCapture}
            disabled={!isStreaming}
          >
            <Camera className="h-4 w-4 mr-2" />
            Capturar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
